import React from 'react';
import { LanguageCode } from '../types';
import { motion } from 'framer-motion';
import { Scale, FileCheck, CheckCircle2, ShieldAlert } from 'lucide-react';

interface TermsPageProps {
    lang: LanguageCode;
}

export const TermsPage = ({ lang }: TermsPageProps) => {
    const t = {
        en: {
            title: "Terms of",
            titleAccent: "Service",
            lastUpdated: "Last updated: October 14, 2026",
            intro: "Welcome to the Nordic Sauna Map. By accessing or using our cultural heritage platform, you agree to comply with and be bound by the following terms and conditions.",
            sections: [
                {
                    title: "Platform Purpose",
                    icon: <Scale className="size-6" />,
                    content: "The Nordic Sauna Map is a non-profit cultural initiative dedicated to documenting, archiving, and promoting traditional and public sauna culture across the Nordic and Baltic region."
                },
                {
                    title: "User Contributions",
                    icon: <FileCheck className="size-6" />,
                    content: "When submitting sauna locations, stories, or photos, you confirm that you own the rights to the content or have permission to share it. Contributions are made public to foster open cultural heritage."
                },
                {
                    title: "Acceptable Use",
                    icon: <CheckCircle2 className="size-6" />,
                    content: "You agree to provide accurate information and refrain from submitting harmful, misleading, commercial spam, or infringing material. We reserve the right to moderate or remove submissions."
                },
                {
                    title: "Disclaimer of Liability",
                    icon: <ShieldAlert className="size-6" />,
                    content: "Sauna details, opening hours, and bathing conditions are provided for cultural and informational purposes. Visitors are responsible for checking local rules, safety guidelines, and current opening status."
                }
            ]
        },
        sv: {
            title: "Användar",
            titleAccent: "villkor",
            lastUpdated: "Senast uppdaterad: 14 oktober 2026",
            intro: "Välkommen till Nordic Sauna Map. Genom att använda vår kulturarvsplattform godkänner du följande villkor och riktlinjer.",
            sections: [
                {
                    title: "Plattformens Syfte",
                    icon: <Scale className="size-6" />,
                    content: "Nordic Sauna Map är ett ideellt kulturarvsprojekt tillägnat att dokumentera, bevara och främja nordisk och baltisk bastukultur."
                },
                {
                    title: "Användarbidrag",
                    icon: <FileCheck className="size-6" />,
                    content: "När du skickar in bastuplatser, berättelser eller fotografier bekräftar du att du äger rättigheterna till materialet eller har tillstånd att dela det för kulturarvsändamål."
                },
                {
                    title: "Godtagbar Användning",
                    icon: <CheckCircle2 className="size-6" />,
                    content: "Du förbinder dig att lämna korrekt information och inte skicka in skadligt, stötande, kommersiellt eller vilseledande innehåll. Vi förbehåller oss rätten att moderera innehåll."
                },
                {
                    title: "Ansvarsfriskrivning",
                    icon: <ShieldAlert className="size-6" />,
                    content: "Uppgifter om bastur, öppettider och badförhållanden tillhandahålls i informationssyfte. Besökare ansvarar själva för att kontrollera lokala säkerhetsföreskrifter och aktuella öppettider."
                }
            ]
        },
        fi: {
            title: "Käyttö",
            titleAccent: "ehdot",
            lastUpdated: "Viimeksi päivitetty: 14. lokakuuta 2026",
            intro: "Tervetuloa Nordic Sauna Mapiin. Käyttämällä kulttuuriperintöalustaamme sitoudut noudattamaan seuraavia ehtoja ja periaatteita.",
            sections: [
                {
                    title: "Alustan Tarkoitus",
                    icon: <Scale className="size-6" />,
                    content: "Nordic Sauna Map on yleishyödyllinen kulttuuriperintöhanke, jonka tavoitteena on dokumentoida, tallentaa ja edistää pohjoismaista saunaperinnettä."
                },
                {
                    title: "Käyttäjien Sisällöt",
                    icon: <FileCheck className="size-6" />,
                    content: "Lähettämällä saunatietoja, tarinoita tai valokuvia vahvistat, että sinulla on oikeus jakaa kyseinen materiaali kulttuurihankkeen käyttöön."
                },
                {
                    title: "Sallittu Käyttö",
                    icon: <CheckCircle2 className="size-6" />,
                    content: "Sitoudut antamaan totuudenmukaisia tietoja etkä lähetä loukkaavaa, harhaanjohtavaa tai kaupallista roskapostia. Ylläpidolla on oikeus tarkastaa ja poistaa sisältöä."
                },
                {
                    title: "Vastuunrajoitus",
                    icon: <ShieldAlert className="size-6" />,
                    content: "Saunojen tiedot ja aukioloajat tarjotaan tiedotustarkoituksessa. Vierailijat vastaavat itse paikallisten turvallisuusohjeiden ja ajantasaisten aukioloaikojen tarkistamisesta."
                }
            ]
        }
    }[lang];

    return (
        <div className="min-h-screen bg-[var(--bg-main)] text-[var(--text-main)] transition-colors duration-300 pt-40 pb-24 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-40">
                <div className="absolute top-[-5%] left-[-10%] w-[50%] h-[50%] bg-blue-100/30 dark:bg-slate-800 rounded-full" />
            </div>

            <div className="max-w-4xl mx-auto px-6 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tight uppercase">
                        {t.title} <br />
                        <span className="text-primary italic">{t.titleAccent}</span>
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 font-bold mb-12 uppercase tracking-widest text-sm">
                        {t.lastUpdated}
                    </p>

                    <div className="p-8 md:p-12 bg-white dark:bg-slate-900 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-2xl mb-16">
                        <p className="text-xl md:text-2xl font-medium leading-relaxed italic text-slate-700 dark:text-slate-300">
                            "{t.intro}"
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {t.sections.map((section, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="p-10 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-xl"
                            >
                                <div className="size-14 rounded-2xl bg-slate-50 dark:bg-slate-800 text-primary flex items-center justify-center mb-8">
                                    {section.icon}
                                </div>
                                <h3 className="text-xl font-black uppercase tracking-tight mb-4 text-slate-900 dark:text-white">
                                    {section.title}
                                </h3>
                                <p className="text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                                    {section.content}
                                </p>
                            </motion.div>
                        ))}
                    </div>

                    <div className="mt-24 p-12 bg-slate-900 rounded-[3rem] text-center">
                        <h2 className="text-2xl font-black text-white uppercase mb-4">Questions?</h2>
                        <p className="text-slate-400 mb-8">Contact us at info@nordicsaunamap.com</p>
                        <a href="mailto:info@nordicsaunamap.com" className="inline-block px-8 py-4 bg-primary text-white rounded-2xl font-bold uppercase tracking-wider text-sm hover:scale-105 transition-transform">
                            Send Email
                        </a>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};
